import { Box, Typography, styled } from '@mui/material';
import React from 'react';

import KpiCard from 'components/charts/kpi-card';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import SparklineChart from 'components/charts/sparkline-chart';
import SeriesMarker from 'views/benchmark-analytics/components/series-marker';
import { BenchmarkSentiment } from 'views/benchmark-analytics/types/benchmark-sentiment.type';
import { sentimentColor } from 'views/benchmark-analytics/utils/benchmark-math';
import { seriesStyle } from 'views/benchmark-analytics/utils/series-style';

type BenchmarkKpiCardProps = {
  label: string;
  value: string; // pre-formatted by the caller
  // A palette slot draws that series' line-plus-mark glyph. A sentiment draws a plain dot ('neutral' for no meaning).
  dot: number | BenchmarkSentiment;
  caption?: string;
  // Drawn in the dot's color, pinned to the bottom of the card. null values leave a gap.
  trend?: Pick<React.ComponentProps<typeof SparklineChart>, 'data' | 'xAxis' | 'valueFormatter'>;
};

const SentimentValue = styled('span', { shouldForwardProp: (prop) => prop !== 'valueColor' })<{ valueColor: string }>(
  ({ valueColor }) => ({
    color: valueColor,
  })
);

const Caption = styled(Typography, { shouldForwardProp: (prop) => prop !== 'chartPalette' })<{ chartPalette: ChartPalette }>(
  ({ chartPalette }) => ({
    fontSize: 13,
    color: chartPalette.text.secondary,
  })
);

const SentimentDot = styled('span', { shouldForwardProp: (prop) => prop !== 'dotColor' })<{ dotColor: string }>(({ dotColor }) => ({
  display: 'inline-block',
  flexShrink: 0,
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: dotColor,
}));

// Not clickable. Color never carries the meaning alone: the caller puts the sign and wording in value and caption.
const BenchmarkKpiCard: React.FC<BenchmarkKpiCardProps> = ({ label, value, dot, caption, trend }) => {
  const palette = useChartPalette();
  const isSentiment = typeof dot === 'string';
  const color = isSentiment ? sentimentColor(palette, dot) : seriesStyle(dot, palette.series.categorical).color;

  return (
    <KpiCard
      label={label}
      value={isSentiment && dot !== 'neutral' ? <SentimentValue valueColor={color}>{value}</SentimentValue> : value}
      marker={isSentiment ? <SentimentDot dotColor={color} aria-hidden /> : <SeriesMarker slot={dot} />}
    >
      {caption && <Caption chartPalette={palette}>{caption}</Caption>}
      {trend && (
        <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="flex-end" minWidth={0}>
          <SparklineChart {...trend} color={color} height={28} disableKeyboardNavigation />
        </Box>
      )}
    </KpiCard>
  );
};

export default BenchmarkKpiCard;
